import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useEffect, useRef, useState } from "react";
import {
  Key, User, LogOut, Shield, TerminalSquare,
  Github, Building2, ArrowLeft, Blocks, Folder,
} from "lucide-react";
import { CaretRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth";
import { hasSeenModelsStep } from "@/lib/onboarding-progress";
import { useMobileLayout } from "@/hooks/use-mobile-layout";

import {
  ActiveImpersonationBanner,
  AdminImpersonationTab,
} from "@/components/settings/AdminImpersonationControls";
import {
  canAccessAdminImpersonationDashboard,
  fetchAdminImpersonationStatus,
  getAdminImpersonationStatusQueryKey,
} from "@/components/settings/admin-impersonation-api";
import { LinearLogoIcon, SlackLogoIcon } from "@/components/brand-icons";

// ---------------------------------------------------------------------------
// Tab components (each in its own file under components/settings/)
// ---------------------------------------------------------------------------

import { SettingsAccountTab } from "@/components/settings/SettingsAccountTab";
import { SettingsOrganizationTab } from "@/components/settings/SettingsOrganizationTab";
import { SettingsGithubTab } from "@/components/settings/SettingsGithubTab";
import { SettingsLinearTab } from "@/components/settings/SettingsLinearTab";
import { SettingsSlackTab } from "@/components/settings/SettingsSlackTab";
import { SettingsMcpTab } from "@/components/settings/SettingsMcpTab";
import { SettingsApiKeysTab } from "@/components/settings/SettingsApiKeysTab";
import { SettingsApiTab } from "@/components/settings/SettingsApiTab";
import { SettingsPersistentStorageTab } from "@/components/settings/SettingsPersistentStorageTab";
import { SettingsUiPreferencesTab } from "@/components/settings/SettingsUiPreferencesTab";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

type SettingsTab =
  | "account"
  | "admin"
  | "organization"
  | "persistent-storage"
  | "github"
  | "linear"
  | "slack"
  | "mcp-skills"
  | "agents-api-keys"
  | "api"
  | "ui-preferences";

const VALID_TABS = new Set<SettingsTab>([
  "account",
  "admin",
  "organization",
  "persistent-storage",
  "github",
  "linear",
  "slack",
  "mcp-skills",
  "agents-api-keys",
  "api",
  "ui-preferences",
]);

const LEGACY_TAB_ALIASES: Record<string, SettingsTab> = {
  team: "organization",
  integrations: "github",
  "git-pull-requests": "github",
  "api-keys": "agents-api-keys",
  preferences: "ui-preferences",
};

export const Route = createFileRoute("/_authed/settings")({
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>): { tab?: SettingsTab } => {
    // OAuth callback params always override `tab` so that in-flight sessions
    // (which may have stored an outdated returnTo) land on the correct tab.
    if (search.linear) {
      return { tab: "linear" };
    }
    if (search.slack) {
      return { tab: "slack" };
    }
    if (search.github_app || search.github_link) {
      return { tab: "github" };
    }
    const tab = typeof search.tab === "string"
      ? LEGACY_TAB_ALIASES[search.tab] ?? search.tab
      : undefined;
    const normalizedTab = tab && VALID_TABS.has(tab as SettingsTab)
      ? (tab as SettingsTab)
      : undefined;
    return { tab: normalizedTab };
  },
});

// ---------------------------------------------------------------------------
// Sidebar navigation data
// ---------------------------------------------------------------------------

function CordLogoTabIcon() {
  return (
    <span className="flex size-3.5 items-center justify-center overflow-hidden rounded-[4px]">
      <img src="/cord-logo.svg" alt="" className="size-full" />
    </span>
  );
}

const TABS: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
  { id: "account", label: "Account", icon: <User className="size-3.5" /> },
  { id: "admin", label: "Admin", icon: <Shield className="size-3.5" /> },
  { id: "organization", label: "Organization", icon: <Building2 className="size-3.5" /> },
  { id: "persistent-storage", label: "Persistent Storage", icon: <Folder className="size-3.5" /> },
  { id: "mcp-skills", label: "MCP & Skills", icon: <Blocks className="size-3.5" /> },
  { id: "agents-api-keys", label: "Models & API Keys", icon: <Key className="size-3.5" /> },
  { id: "api", label: "Cord API", icon: <CordLogoTabIcon /> },
  { id: "ui-preferences", label: "UI Preferences", icon: <TerminalSquare className="size-3.5" /> },
  { id: "github", label: "GitHub", icon: <Github className="size-3.5" /> },
  { id: "linear", label: "Linear", icon: <LinearLogoIcon className="size-3.5" /> },
  { id: "slack", label: "Slack", icon: <SlackLogoIcon className="size-3.5" /> },
];

/**
 * Tabs grouped into logical sections for the sidebar nav.
 * Each section has a heading and a list of tab IDs.
 */
const TAB_SECTIONS: { heading: string; tabs: SettingsTab[] }[] = [
  {
    heading: "General",
    tabs: ["account", "ui-preferences"],
  },
  {
    heading: "Administration",
    tabs: ["admin"],
  },
  {
    heading: "Workspace",
    tabs: ["organization", "persistent-storage", "agents-api-keys", "mcp-skills", "api"],
  },
  {
    heading: "Integrations",
    tabs: ["github", "linear", "slack"],
  },
];

/** Look up a tab definition by id. */
function findTab(id: SettingsTab) {
  return TABS.find((t) => t.id === id);
}

// ---------------------------------------------------------------------------
// Main Settings Page (thin shell)
// ---------------------------------------------------------------------------

function SettingsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { tab } = Route.useSearch();
  const activeTab: SettingsTab = tab || "account";
  const isMobileLayout = useMobileLayout();

  const { data: session } = authClient.useSession();
  const { data: impersonationStatus } = useQuery({
    queryKey: getAdminImpersonationStatusQueryKey({
      sessionUserId: session?.user?.id,
    }),
    queryFn: fetchAdminImpersonationStatus,
    enabled: Boolean(session?.user?.id),
  });

  // ---- Onboarding redirect after GitHub App callback (fallback) ----
  // The primary flow now uses origin=onboarding in the state cookie so
  // the backend redirects directly to /getting-started. This is a safety
  // net for cases where the origin wasn't set (e.g. old cookies, direct
  // GitHub App install from the GitHub UI).
  const isGitHubCallbackRef = useRef(
    new URL(window.location.href).searchParams.get("github_app") === "connected",
  );

  useEffect(() => {
    if (!isGitHubCallbackRef.current) return;
    isGitHubCallbackRef.current = false;

    // Only redirect if onboarding hasn't been completed
    const userId = session?.user?.id ?? "";
    if (hasSeenModelsStep(userId)) return;

    navigate({ to: "/getting-started" });
  }, [navigate, session?.user?.id]);

  // Fetch org info for the left sidebar
  const hasOrg = !!session?.session?.activeOrganizationId;

  const { data: activeMember } = useQuery({
    queryKey: ["active-member"],
    queryFn: async () => {
      const { data } = await authClient.organization.getActiveMember();
      return data;
    },
    enabled: hasOrg,
  });

  const showAdminTab = canAccessAdminImpersonationDashboard({
    sessionAppRole: session?.user?.appRole,
    status: impersonationStatus,
  });

  const visibleTabSections = useMemo(
    () => TAB_SECTIONS
      .map((section) => ({
        ...section,
        tabs: section.tabs.filter((tabId) => tabId !== "admin" || showAdminTab),
      }))
      .filter((section) => section.tabs.length > 0),
    [showAdminTab],
  );

  const setTab = (t: SettingsTab) => {
    navigate({ search: { tab: t } });
  };

  // Clean up transient OAuth callback params from the URL so they don't
  // persist across refreshes or back/forward navigation.
  useEffect(() => {
    const transientKeys = ["github_app", "scope", "linear", "slack", "github_link", "github_username", "reason"];
    const nextUrl = new URL(window.location.href);
    let changed = false;
    for (const key of transientKeys) {
      if (nextUrl.searchParams.has(key)) {
        nextUrl.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState({}, "", nextUrl.toString());
    }
  }, []);

  // Mobile two-level navigation state
  const [mobileShowContent, setMobileShowContent] = useState(false);

  // When navigating back on mobile, reset the content view
  const handleMobileBack = () => {
    setMobileShowContent(false);
  };

  return (
    <div className="h-full overflow-y-auto"
      style={isMobileLayout ? {
        paddingTop: "var(--safe-area-top, 0px)",
        paddingBottom: "var(--safe-area-bottom, 0px)",
      } : undefined}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 lg:py-10 animate-[fade-in_0.2s_ease-out]">
        {/* Header row */}
        {isMobileLayout ? (
          <div className="mb-6 flex items-center gap-3">
            <button
              type="button"
              onClick={mobileShowContent ? handleMobileBack : () => navigate({ to: "/" })}
              aria-label={mobileShowContent ? "Back to settings list" : "Go back"}
              className="flex size-10 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors active:bg-zinc-800"
            >
              <ArrowLeft className="size-5" />
            </button>
            <h1 className="text-[17px] font-semibold text-zinc-200">
              {mobileShowContent ? findTab(activeTab)?.label ?? "Settings" : "Settings"}
            </h1>
          </div>
        ) : (
          <div className="mb-8 flex items-center gap-3">
            <button
              onClick={() => navigate({ to: "/" })}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-300 transition-colors"
            >
              <ArrowLeft className="size-3" />
              Back
            </button>
            <span className="text-xs text-zinc-700">/</span>
            <h1 className="text-sm font-medium text-zinc-200">Settings</h1>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-12">
          {/* ---- Left Sidebar ---- */}
          <aside className="hidden lg:flex lg:flex-col w-52 shrink-0 sticky top-10 self-start max-h-[calc(100vh-2.5rem)] overflow-y-auto">
            {/* Compact user identity */}
            <div className="flex items-center gap-2.5 mb-8">
              {session?.user?.image ? (
                <img
                  src={session.user.image}
                  alt=""
                  className="size-8 rounded-full ring-1 ring-white/[0.08]"
                />
              ) : (
                <div className="flex size-8 items-center justify-center rounded-full bg-white/[0.06] text-xs font-medium text-zinc-300">
                  {(session?.user?.name ?? "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-200">
                  {session?.user?.name}
                </div>
                <div className="truncate text-xs text-zinc-400">
                  {session?.user?.email}
                </div>
              </div>
              {activeMember?.role && (
                <span
                  className={cn(
                    "shrink-0 rounded px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide",
                    activeMember.role === "owner"
                      ? "bg-amber-500/10 text-amber-400/80"
                      : activeMember.role === "admin"
                        ? "bg-blue-500/10 text-blue-400/80"
                        : "bg-white/[0.04] text-zinc-400",
                  )}
                >
                  {activeMember.role}
                </span>
              )}
            </div>

            {/* Grouped navigation */}
            <nav className="flex flex-col gap-6 flex-1">
              {visibleTabSections.map((section) => (
                <div key={section.heading}>
                  <div className="mb-1.5 px-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                    {section.heading}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {section.tabs.map((tabId) => {
                      const t = findTab(tabId);
                      if (!t) return null;
                      return (
                        <button
                          key={t.id}
                          onClick={() => setTab(t.id)}
                          className={cn(
                            "group inline-flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-left text-sm font-medium transition-all",
                            activeTab === t.id
                              ? "bg-white/[0.07] text-zinc-100 shadow-[inset_0_0.5px_0_rgba(255,255,255,0.06)]"
                              : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-200",
                          )}
                        >
                          <span className={cn(
                            "flex size-5 items-center justify-center rounded-md transition-colors",
                            activeTab === t.id
                              ? "text-zinc-100"
                              : "text-zinc-400 group-hover:text-zinc-300",
                          )}>
                            {t.icon}
                          </span>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Sign out — tucked at the bottom */}
            <button
              onClick={() =>
                authClient.signOut({
                  fetchOptions: {
                    onSuccess: () => {
                      window.location.href = "/login";
                    },
                  },
                })
              }
              className="mt-6 inline-flex w-full items-center gap-2 rounded-lg px-2.5 py-[7px] text-sm font-medium text-zinc-400 transition-colors hover:bg-white/[0.04] hover:text-zinc-200"
            >
              <LogOut className="size-3.5" />
              Sign out
            </button>
          </aside>

          {/* ---- Right Content ---- */}
          <div className="min-w-0 flex-1 lg:border-l lg:border-white/[0.04] lg:pl-12">
            <ActiveImpersonationBanner sessionUserId={session?.user?.id} />

            {/* Mobile: Full list navigation (iOS Settings style) */}
            {isMobileLayout && !mobileShowContent && (
              <div className="space-y-6">
                {visibleTabSections.map((section) => (
                  <div key={section.heading}>
                    <h3 className="mb-1.5 px-1 text-[13px] font-medium uppercase tracking-wider text-zinc-500">
                      {section.heading}
                    </h3>
                    <div className="overflow-hidden rounded-xl bg-zinc-900/60">
                      {section.tabs.map((tabId, idx) => {
                        const t = findTab(tabId)
                        if (!t) return null
                        return (
                          <button
                            key={t.id}
                            onClick={() => { setTab(t.id); setMobileShowContent(true) }}
                            className={cn(
                              "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-zinc-800",
                              idx !== 0 && "border-t border-zinc-800/60",
                            )}
                          >
                            <span className="flex size-7 items-center justify-center rounded-md bg-zinc-800 text-zinc-300">
                              {t.icon}
                            </span>
                            <span className="flex-1 text-[15px] text-zinc-200">{t.label}</span>
                            <CaretRight className="size-4 text-zinc-600" />
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mobile: Tab content (level 2) */}
            {isMobileLayout && mobileShowContent && (
              <div className="animate-[fade-in_0.15s_ease-out]" key={activeTab}>
                {activeTab === "account" && <SettingsAccountTab />}
                {activeTab === "admin" && (
                  <AdminImpersonationTab
                    sessionUserId={session?.user?.id}
                    sessionAppRole={session?.user?.appRole}
                  />
                )}
                {activeTab === "organization" && <SettingsOrganizationTab />}
                {activeTab === "persistent-storage" && <SettingsPersistentStorageTab />}
                {activeTab === "github" && <SettingsGithubTab />}
                {activeTab === "linear" && <SettingsLinearTab />}
                {activeTab === "slack" && <SettingsSlackTab />}
                {activeTab === "mcp-skills" && <SettingsMcpTab />}
                {activeTab === "agents-api-keys" && <SettingsApiKeysTab />}
                {activeTab === "api" && <SettingsApiTab />}
                {activeTab === "ui-preferences" && <SettingsUiPreferencesTab />}
              </div>
            )}

            {/* Desktop: Tab Content */}
            {!isMobileLayout && (
              <div className="animate-[fade-in_0.15s_ease-out]" key={activeTab}>
                {activeTab === "account" && <SettingsAccountTab />}
                {activeTab === "admin" && (
                  <AdminImpersonationTab
                    sessionUserId={session?.user?.id}
                    sessionAppRole={session?.user?.appRole}
                  />
                )}
                {activeTab === "organization" && <SettingsOrganizationTab />}
                {activeTab === "persistent-storage" && <SettingsPersistentStorageTab />}
                {activeTab === "github" && <SettingsGithubTab />}
                {activeTab === "linear" && <SettingsLinearTab />}
                {activeTab === "slack" && <SettingsSlackTab />}
                {activeTab === "mcp-skills" && <SettingsMcpTab />}
                {activeTab === "agents-api-keys" && <SettingsApiKeysTab />}
                {activeTab === "api" && <SettingsApiTab />}
                {activeTab === "ui-preferences" && <SettingsUiPreferencesTab />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
